/**
 * Utility functions to manage human agent request throttle
 * Prevents users from creating multiple cases/leads within 5 minutes
 */

const THROTTLE_KEY = 'humanAgentRequestTimestamp';
const THROTTLE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

/**
 * Checks if the user is currently in throttle period
 * @returns {boolean} - true if user is throttled, false otherwise
 */
export const isThrottled = (): boolean => {
  const timestamp = localStorage.getItem(THROTTLE_KEY);
  
  if (!timestamp) {
    return false;
  }
  
  const lastRequestTime = parseInt(timestamp, 10);
  const currentTime = Date.now();
  const timeDifference = currentTime - lastRequestTime;
  
  return timeDifference < THROTTLE_DURATION;
};

/**
 * Gets the remaining throttle time in milliseconds
 * @returns {number} - remaining time in milliseconds, 0 if not throttled
 */
export const getRemainingThrottleTime = (): number => {
  const timestamp = localStorage.getItem(THROTTLE_KEY);
  
  if (!timestamp) {
    return 0;
  }
  
  const lastRequestTime = parseInt(timestamp, 10);
  const currentTime = Date.now();
  const timeDifference = currentTime - lastRequestTime;
  const remainingTime = THROTTLE_DURATION - timeDifference;
  
  return remainingTime > 0 ? remainingTime : 0;
};

/**
 * Gets remaining time formatted as "X minutes Y seconds"
 * @returns {string} - formatted remaining time
 */
export const getFormattedRemainingTime = (): string => {
  const remainingMs = getRemainingThrottleTime();
  
  if (remainingMs === 0) {
    return '';
  }
  
  const minutes = Math.floor(remainingMs / 60000);
  const seconds = Math.floor((remainingMs % 60000) / 1000);
  
  if (minutes > 0 && seconds > 0) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ${seconds} second${seconds !== 1 ? 's' : ''}`;
  } else if (minutes > 0) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  } else {
    return `${seconds} second${seconds !== 1 ? 's' : ''}`;
  }
};

/**
 * Sets the throttle timestamp to current time
 * Should be called when user clicks the human agent icon
 */
export const setThrottle = (): void => {
  const currentTime = Date.now();
  localStorage.setItem(THROTTLE_KEY, currentTime.toString());
};

/**
 * Clears the throttle (mainly for testing purposes)
 */
export const clearThrottle = (): void => {
  localStorage.removeItem(THROTTLE_KEY);
};

